import { log } from "@grafit/shared/observability/sentry-front";
import { authClient } from "./auth-client";
import { clearSSOAttempt, SSO_ERROR_PATH, startSSOAttempt } from "./utils/sso-state";
import { storeLastLoginDate } from "./utils/store-last-login";

// better-auth keeps a single OAuth `state` cookie per browser: a second sign-in request overwrites it
// while the browser follows the first redirect, which then fails with `state_mismatch`.
interface SSOAuthorizationParams {
  providerId: string;
  callbackURL: string;
  userEmail: string;
}

let inFlightRequest: { params: SSOAuthorizationParams; promise: Promise<void> } | undefined;

const isSameAuthorization = (a: SSOAuthorizationParams, b: SSOAuthorizationParams): boolean =>
  a.providerId === b.providerId && a.callbackURL === b.callbackURL && a.userEmail === b.userEmail;

export const signInWithSSO = ({
  providerId,
  callbackPath = window.location.pathname,
  userEmail,
}: {
  providerId: string;
  callbackPath?: string;
  userEmail: string;
}): Promise<void> => {
  const params = { providerId, callbackURL: new URL(callbackPath, window.location.origin).href, userEmail };
  if (inFlightRequest) {
    if (isSameAuthorization(inFlightRequest.params, params)) {
      return inFlightRequest.promise;
    }
    // A second request would overwrite the `state` cookie of the in-flight one, so refuse it explicitly.
    log("SSO sign-in rejected: another one is in progress", "warn", {
      in_flight_callback_url: inFlightRequest.params.callbackURL,
      in_flight_email: inFlightRequest.params.userEmail,
      in_flight_sso_provider_id: inFlightRequest.params.providerId,
      callback_url: params.callbackURL,
      email: params.userEmail,
      sso_provider_id: params.providerId,
    });
    return Promise.reject(new Error("SSO sign-in already in progress"));
  }
  const promise = requestSSOAuthorization(params);
  inFlightRequest = { params, promise };
  const clearInFlightRequest = (): void => {
    inFlightRequest = undefined;
  };
  void promise.then(clearInFlightRequest, clearInFlightRequest);
  return promise;
};

const requestSSOAuthorization = ({ providerId, callbackURL, userEmail }: SSOAuthorizationParams): Promise<void> => {
  const errorCallbackURL = new URL(SSO_ERROR_PATH, window.location.origin).href;
  const { attempt, previousAttempt } = startSSOAttempt(providerId);
  const telemetryContext = {
    callback_url: callbackURL,
    email: userEmail,
    error_callback_url: errorCallbackURL,
    sso_attempt_id: attempt.id,
    sso_attempt_started_at: attempt.startedAt,
    sso_provider_id: providerId,
  };

  if (previousAttempt) {
    log("Overlapping SSO authorization attempt detected", "warn", {
      email: userEmail,
      previous_attempt_id: previousAttempt.id,
      previous_attempt_started_at: previousAttempt.startedAt,
      previous_sso_provider_id: previousAttempt.providerId,
      sso_attempt_id: attempt.id,
      sso_attempt_started_at: attempt.startedAt,
      sso_provider_id: providerId,
    });
  }

  return authClient.signIn
    .sso({
      providerId,
      email: userEmail,
      callbackURL,
      errorCallbackURL,
    })
    .then((result) => {
      if (result.error) {
        throw new Error(result.error.message);
      }
      log("SSO authorization request succeeded", "info", telemetryContext);
      storeLastLoginDate(userEmail);
      return;
    })
    .catch((error: unknown) => {
      const normalizedError = error instanceof Error ? error : new Error(String(error));
      clearSSOAttempt(attempt.id);
      log("SSO authorization request failed", "error", {
        ...telemetryContext,
        error_message: normalizedError.message,
        error_name: normalizedError.name,
      });
      throw normalizedError;
    });
};
